import { useMutationSessionPackSubscriptionSessionsLeft } from "@gymflow/api";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmModal } from "../../../templates";
import { Button, PlusIcon } from "../..";
import { MinusIcon } from "../../icons/MinusIcon";

type AdjustQuantityModalProps = {
  hide: () => void;
  totalCredits: number;
  remaining?: number;
  subscriptionId: number;
  userMemberId: string;
};

export const AdjustQuantityModal = ({
  hide,
  totalCredits,
  remaining,
  subscriptionId,
  userMemberId,
}: AdjustQuantityModalProps) => {
  const [sessionRemaining, setSessionRemaining] = useState(remaining ?? 0);

  const { t } = useTranslation();
  const { toast } = useContext(ToastContext);
  const { api } = useGymflowModels();

  const { mutateAsync: mutateAsyncSessionPackSubscriptionSessionsLeft } =
    useMutationSessionPackSubscriptionSessionsLeft({ api });

  const onConfirm = useCallback(async () => {
    await mutateAsyncSessionPackSubscriptionSessionsLeft({
      quantity: sessionRemaining,
      subscriptionId,
      userMemberId,
    });
    toast({
      message: t(
        "components.creditPackItem.toasts.creditPackSuccessfullyUpdated",
      ),
    });
    hide();
  }, [
    hide,
    mutateAsyncSessionPackSubscriptionSessionsLeft,
    sessionRemaining,
    subscriptionId,
    t,
    toast,
    userMemberId,
  ]);

  return (
    <ConfirmModal
      title={t("components.creditPackItem.modal.adjustQuantityModal.title")}
      onCancel={() => hide()}
      onConfirm={async () => await onConfirm()}
    >
      {t("components.creditPackItem.modal.adjustQuantityModal.description")}
      <div className="mt-4 flex flex-row items-center justify-center gap-2.5">
        <Button
          className="size-11 p-0"
          onClick={() => setSessionRemaining((e) => e - 1)}
          disabled={sessionRemaining === 0}
        >
          <MinusIcon className="size-5" />
        </Button>
        <div className="text-base font-medium text-gray-950">
          {sessionRemaining}
        </div>
        <Button
          className="size-11 p-0"
          onClick={() => setSessionRemaining((e) => e + 1)}
          disabled={sessionRemaining >= totalCredits}
        >
          <PlusIcon className="size-5" pathClassName="stroke-gray-950" />
        </Button>
      </div>
    </ConfirmModal>
  );
};
